/**
 * Public content registry. Pages call getContent(lang) and render the result.
 *
 * Translation files live in ./translations/{lang}.ts and are produced by
 * scripts/translate-content.mjs (using Lovable AI gateway). If a language
 * file is missing or incomplete, English is used as a fallback per-field.
 */
import { EN_CONTENT, type SiteContent } from "./source";
import { EL_CONTENT } from "./translations/el";
import { ES_CONTENT } from "./translations/es";
import { FR_CONTENT } from "./translations/fr";
import { DE_CONTENT } from "./translations/de";
import { IT_CONTENT } from "./translations/it";
import { JA_CONTENT } from "./translations/ja";

const REGISTRY: Record<string, Partial<SiteContent>> = {
  en: EN_CONTENT,
  el: EL_CONTENT,
  es: ES_CONTENT,
  fr: FR_CONTENT,
  de: DE_CONTENT,
  it: IT_CONTENT,
  ja: JA_CONTENT,
};

function mergeWithFallback<T>(en: T, partial: Partial<T> | undefined): T {
  if (!partial) return en;
  const out: any = Array.isArray(en) ? [...(en as any)] : { ...(en as any) };
  for (const k of Object.keys(partial as any)) {
    const v = (partial as any)[k];
    const enV = (en as any)[k];
    if (v == null || v === "") continue;
    if (Array.isArray(v) && Array.isArray(enV)) {
      out[k] = v.map((item, i) =>
        typeof item === "object" && item !== null ? { ...(enV[i] || {}), ...item } : item,
      );
      // pad with EN entries beyond the translated length
      if (enV.length > v.length) {
        for (let i = v.length; i < enV.length; i++) out[k].push(enV[i]);
      }
    } else if (typeof v === "object" && typeof enV === "object" && enV !== null) {
      out[k] = mergeWithFallback(enV, v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export function getContent(lang: string | undefined): SiteContent {
  const base = (lang || "en").split("-")[0];
  const partial = REGISTRY[base];
  if (!partial || base === "en") return EN_CONTENT;
  return {
    landing: mergeWithFallback(EN_CONTENT.landing, partial.landing),
    blogIndex: mergeWithFallback(EN_CONTENT.blogIndex, partial.blogIndex),
    blogPostUI: mergeWithFallback(EN_CONTENT.blogPostUI, partial.blogPostUI),
    blogPosts: (() => {
      const enPosts = EN_CONTENT.blogPosts;
      const trPosts = partial.blogPosts || [];
      const trBySlug = new Map(trPosts.map((p) => [p.slug, p]));
      return enPosts.map((p) => {
        const tr = trBySlug.get(p.slug);
        return tr ? { ...p, ...tr } : p;
      });
    })(),
    terms: mergeWithFallback(EN_CONTENT.terms, partial.terms),
    privacy: mergeWithFallback(EN_CONTENT.privacy, partial.privacy),
    refund: mergeWithFallback(EN_CONTENT.refund, partial.refund),
  };
}

export type { SiteContent } from "./source";
